import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import { useTranslation } from 'react-i18next';

const Pricing = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
  
  const plans = [
    {
      name: t('pricing.basicPresence'),
      price: '$9',
      period: t('pricing.perMonth'),
      features: [
        t('pricing.voiceSimulation'),
        t('pricing.basicStyleMapping'),
        t('pricing.limitedMemoryAnchoring'),
        t('pricing.standardPrivacyTools')
      ],
      cta: t('pricing.startBasic'),
      highlighted: false
    },
    {
      name: t('pricing.enhancedPersona'),
      price: '$29',
      period: t('pricing.perMonth'),
      features: [
        t('pricing.fullStyleToolkit'),
        t('pricing.extendedMemoryGraph'),
        t('pricing.behavioralTuning'),
        t('pricing.ethicalCoreCustomization')
      ],
      cta: t('pricing.upgradeToEnhanced'),
      highlighted: true
    },
    {
      name: t('pricing.legacyContinuum'),
      price: '$79',
      period: t('pricing.perMonth'),
      features: [
        t('pricing.lifeStoryStructuring'),
        t('pricing.legacyModeDataHandling'),
        t('pricing.multiGenerationMemory'),
        t('pricing.premiumPrivacyControls')
      ],
      cta: t('pricing.buildYourLegacy'),
      highlighted: false
    },
    {
      name: t('pricing.enterpriseIdentity'),
      price: t('pricing.custom'),
      period: '',
      features: [
        t('pricing.apiAccess'),
        t('pricing.multiProfileSystems'),
        t('pricing.secureDeployment'),
        t('pricing.dedicatedSupport')
      ],
      cta: t('pricing.contactSales'),
      highlighted: false
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      <PageTitle title="Pricing - Choose Your Presence Plan" />
      {/* BLOCK 1 - HERO */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32 bg-gradient-to-b from-black via-[#0B1F2E]/30 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold mb-8 reveal-on-scroll">
            {t('pricing.title')}
          </h1>
        </div>
      </section>

      {/* BLOCK 2 - PRICING GRID */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, idx) => (
              <Card 
                key={idx}
                className={`bg-gradient-to-b from-gray-900 to-gray-800 ${
                  plan.highlighted 
                    ? 'border-cyan-500 shadow-xl shadow-cyan-500/20 scale-105 animate-heartbeat' 
                    : 'border-cyan-500/20 hover:border-cyan-500/50'
                } transition-all duration-300 hover:scale-105 relative overflow-hidden reveal-on-scroll lift-on-hover`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-cyan-600" />
                )}
                <CardHeader className="text-center pb-8 pt-10">
                  <CardTitle className="text-2xl font-bold mb-4">{plan.name}</CardTitle>
                  <div className="space-y-2">
                    <div className="text-5xl font-bold text-cyan-400 counter-animation">{plan.price}</div>
                    {plan.period && <div className="text-gray-400 text-sm">{plan.period}</div>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-8">
                  <ul className="space-y-4">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start space-x-3">
                        <div className="w-5 h-5 bg-cyan-500/20 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                          <Check className="text-cyan-400" size={14} />
                        </div>
                        <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/create">
                    <Button 
                      className={`w-full ripple-effect ${
                        plan.highlighted
                          ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white'
                          : 'bg-gray-800 hover:bg-gray-700 text-cyan-400 border border-cyan-500/30'
                      }`}
                      size="lg"
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
